import { useEffect, useRef, useState } from "react"
import Modal from "./Modal"
import Icon from "../../components/Icon"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { formatMoney } from "../../utils/format"
import api from "../../utils/api"

const iconInputClass =
  "w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"

const searchInputClass =
  "w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"

const CATEGORY_KIND_META = {
  generic: { label: "Other", icon: "tag-fill", swatch: "bg-slate-100 text-slate-500" },
  water: { label: "Water", icon: "droplet-fill", swatch: "bg-sky-100 text-sky-600" },
  trash: { label: "Trash", icon: "trash3-fill", swatch: "bg-amber-100 text-amber-600" },
}

const emptyForm = {
  // customer
  customerSearch: "",
  customerId: "",
  name: "",
  phone: "",
  idNumber: "",
  pin: "",
  // rental scope
  propertyId: "",
  unitId: "",
  // bill
  amount: "",
  categoryId: "", // "" = unselected, "__new__" = create new, otherwise an id
  categoryName: "",
  categoryKind: "generic",
  dueDate: "",
  description: "",
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

function SectionHeader({ icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100 text-teal-700">
        <Icon name={icon} size="0.75rem" />
      </span>
      <h4 className="text-[13px] font-bold text-slate-700">{children}</h4>
    </div>
  )
}

function FieldIcon({ icon }) {
  return (
    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
      <Icon name={icon} size="0.8rem" />
    </span>
  )
}

// Searchable "select or create" combobox for categories — replaces a plain
// <select> so creating a brand new category isn't just another option mixed
// in with real data.
function CategoryCombobox({ categories, categoryId, categoryName, categoryKind, onSelectExisting, onCreateNew }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  // "Creating" is its own explicit step, reached through an always-visible
  // button, rather than something only discovered by typing a name that
  // happens not to match anything — that was hard to find.
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState("")
  const [draftKind, setDraftKind] = useState("generic")
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    const onEscape = (event) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    document.addEventListener("keydown", onEscape)
    return () => {
      document.removeEventListener("mousedown", onClickOutside)
      document.removeEventListener("keydown", onEscape)
    }
  }, [open])

  // Always re-open on the search view, not mid-way through creating.
  useEffect(() => {
    if (open) {
      setCreating(false)
      setNewName("")
    }
  }, [open])

  const trimmedQuery = query.trim()
  const filtered = categories.filter((category) => category.name.toLowerCase().includes(trimmedQuery.toLowerCase()))

  const trimmedNewName = newName.trim()
  const duplicateName = categories.some((category) => category.name.toLowerCase() === trimmedNewName.toLowerCase())

  const selected =
    categoryId === "__new__"
      ? { name: categoryName || "New category", kind: categoryKind }
      : categories.find((category) => String(category._id) === String(categoryId))
  const selectedMeta = CATEGORY_KIND_META[selected?.kind || "generic"]

  const selectExisting = (category) => {
    onSelectExisting(category)
    setQuery("")
    setOpen(false)
  }

  const startCreating = () => {
    setNewName(trimmedQuery)
    setCreating(true)
  }

  const createNew = () => {
    if (!trimmedNewName || duplicateName) return
    onCreateNew({ name: trimmedNewName, kind: draftKind })
    setQuery("")
    setNewName("")
    setDraftKind("generic")
    setCreating(false)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className="relative">
      {open && !creating ? (
        // While actively searching, this box IS the search field — not a
        // second one stacked below an unchanged closed-state box, which
        // looked like two duplicate search bars.
        <div className="flex w-full items-center gap-2 rounded-xl border border-teal-500 bg-white px-3 py-2.5 ring-2 ring-teal-100">
          <Icon name="search" size="0.8rem" className="shrink-0 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search categories…"
            className="w-full min-w-0 bg-transparent text-left text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          <Icon name="chevron-up" size="0.7rem" className="shrink-0 text-slate-400" />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2.5 text-left text-sm transition-colors ${
            open ? "border-teal-500 ring-2 ring-teal-100" : "border-slate-300 hover:border-slate-400"
          }`}
        >
          {creating ? (
            <span className="text-slate-500">New category</span>
          ) : selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${selectedMeta.swatch}`}>
                <Icon name={selectedMeta.icon} size="0.6rem" />
              </span>
              <span className="truncate font-semibold text-slate-800">{selected.name}</span>
            </span>
          ) : (
            <span className="text-slate-400">Select or create category</span>
          )}
          <Icon name={open ? "chevron-up" : "chevron-down"} size="0.7rem" className="shrink-0 text-slate-400" />
        </button>
      )}

      {open && (
        <div className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          {creating ? (
            <div className="p-2.5">
              <div className="mb-2 flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                  <Icon name="plus-circle-fill" size="0.8rem" className="text-teal-600" />
                  New category
                </p>
                <button
                  type="button"
                  onClick={() => setCreating(false)}
                  className="flex items-center gap-1 text-xs font-bold text-teal-700"
                >
                  <Icon name="arrow-left" size="0.7rem" />
                  Back to search
                </button>
              </div>
              <input
                autoFocus
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Category name"
                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none"
              />
              {duplicateName && trimmedNewName && (
                <p className="mt-1 text-xs font-medium text-amber-600">
                  “{trimmedNewName}” already exists — search for it instead.
                </p>
              )}
              <div className="mt-2 flex gap-1.5">
                {Object.entries(CATEGORY_KIND_META).map(([kind, meta]) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setDraftKind(kind)}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-lg border py-1.5 text-xs font-semibold transition-colors ${
                      draftKind === kind
                        ? "border-teal-500 bg-teal-50 text-teal-700"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    <Icon name={meta.icon} size="0.7rem" />
                    {meta.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={createNew}
                disabled={!trimmedNewName || duplicateName}
                className="mt-2 w-full rounded-lg bg-teal-600 py-1.5 text-xs font-bold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                Add “{trimmedNewName || "category"}”
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={startCreating}
                className="flex w-full items-center gap-1.5 border-b border-slate-100 bg-teal-50/60 px-3 py-2.5 text-left text-xs font-bold text-teal-700 hover:bg-teal-50"
              >
                <Icon name="plus-circle-fill" size="0.8rem" />
                Create a new category
              </button>

              {filtered.length > 0 && (
                <div className="max-h-36 overflow-y-auto py-1">
                  {filtered.map((category) => {
                    const meta = CATEGORY_KIND_META[category.kind] || CATEGORY_KIND_META.generic
                    return (
                      <button
                        key={category._id}
                        type="button"
                        onClick={() => selectExisting(category)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-teal-50"
                      >
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${meta.swatch}`}>
                          <Icon name={meta.icon} size="0.6rem" />
                        </span>
                        <span className="truncate font-medium text-slate-700">{category.name}</span>
                      </button>
                    )
                  })}
                </div>
              )}

              {filtered.length === 0 && (
                <p className="px-3 py-3 text-xs text-slate-400">
                  {trimmedQuery ? `No category matches “${trimmedQuery}”.` : "No categories yet."}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// One-step "Issue bill": find an existing customer or register one inline,
// pick or create a category, set an amount. The bill account is found or
// created automatically on submit.
function AddBillModal({
  providerId,
  providerName,
  categories = [],
  unitScopeEnabled = false,
  properties = [],
  initialPropertyId = "",
  initialUnitId = "",
  onClose,
  onCreate,
  onCreateBill,
  onSearchCustomers,
}) {
  const submitBill = onCreateBill || onCreate
  const [form, setForm] = useState(emptyForm)
  const [customerMode, setCustomerMode] = useState("search") // "search" | "new"
  const [units, setUnits] = useState([])
  const [unitsLoading, setUnitsLoading] = useState(false)
  const [unitsError, setUnitsError] = useState("")
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const appliedInitialUnitRef = useRef("")

  const hasLinkedCustomer = Boolean(form.customerId)
  const isNewCategory = form.categoryId === "__new__"

  useEffect(() => {
    if (!unitScopeEnabled) return
    const fallbackPropertyId = initialPropertyId || properties[0]?._id || ""
    if (!fallbackPropertyId) return
    setForm((current) => current.propertyId ? current : { ...current, propertyId: fallbackPropertyId })
  }, [unitScopeEnabled, initialPropertyId, properties])

  useEffect(() => {
    if (!unitScopeEnabled || !providerId || !form.propertyId) {
      setUnits([])
      setUnitsError("")
      return
    }

    let active = true
    setUnitsLoading(true)
    setUnitsError("")
    api
      .get(`/ut-bills/providers/${encodeURIComponent(providerId)}/units?property=${encodeURIComponent(form.propertyId)}`)
      .then((response) => {
        if (!active) return
        const list = Array.isArray(response?.data?.units) ? response.data.units : []
        setUnits(list)
        setForm((current) => {
          const initialUnit = initialUnitId
            ? list.find((unit) => String(unit._id) === String(initialUnitId))
            : null
          const initialUnitKey = initialUnit ? `${form.propertyId}:${initialUnitId}` : ""
          if (initialUnit && appliedInitialUnitRef.current !== initialUnitKey) {
            const tenant = initialUnit.tenant
            appliedInitialUnitRef.current = initialUnitKey
            return {
              ...current,
              unitId: initialUnit._id || "",
              customerId: tenant?._id || "",
              customerSearch: tenant?.name || "",
              name: tenant?.name || "",
              phone: tenant?.phone || "",
              idNumber: tenant?.idNumber || "",
              pin: "",
            }
          }

          return current.unitId && list.some((unit) => String(unit._id) === String(current.unitId))
            ? current
            : { ...current, unitId: "" }
        })
      })
      .catch((err) => {
        if (!active) return
        setUnits([])
        setUnitsError(getApiErrorMessage(err, "Failed to load units."))
      })
      .finally(() => {
        if (active) setUnitsLoading(false)
      })

    return () => {
      active = false
    }
  }, [unitScopeEnabled, providerId, form.propertyId, initialUnitId])

  // Live, debounced customer search — no need for an explicit search button.
  useEffect(() => {
    if (hasLinkedCustomer || customerMode !== "search") {
      setResults([])
      return
    }
    const query = form.customerSearch.trim()
    if (query.length < 2) {
      setResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const matches = await onSearchCustomers?.(query)
        if (!cancelled) setResults(matches || [])
      } catch (err) {
        if (!cancelled) {
          setResults([])
          setError(getApiErrorMessage(err, "Failed to search customers."))
        }
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [form.customerSearch, hasLinkedCustomer, customerMode, onSearchCustomers])

  const selectCustomer = (customer) => {
    setForm((current) => ({
      ...current,
      customerId: customer?._id || "",
      customerSearch: customer?.name || "",
      name: customer?.name || "",
      pin: "",
    }))
    setResults([])
  }

  const selectUnit = (unitId) => {
    const unit = units.find((item) => String(item._id) === String(unitId))
    const tenant = unit?.tenant
    setCustomerMode("search")
    setResults([])
    setForm((current) => ({
      ...current,
      unitId: unit?._id || "",
      customerId: tenant?._id || "",
      customerSearch: tenant?.name || "",
      name: tenant?.name || "",
      phone: tenant?.phone || "",
      idNumber: tenant?.idNumber || "",
      pin: "",
    }))
  }

  const clearLinkedCustomer = () => {
    setForm((current) => ({
      ...current,
      unitId: unitScopeEnabled ? "" : current.unitId,
      customerId: "",
      customerSearch: "",
      name: "",
      phone: "",
      idNumber: "",
      pin: "",
    }))
  }

  const switchMode = (mode) => {
    setCustomerMode(mode)
    setError("")
    setResults([])
    setForm((current) => ({
      ...current,
      unitId: unitScopeEnabled ? "" : current.unitId,
      customerId: "",
      name: mode === "new" ? current.customerSearch : "",
      phone: "",
      idNumber: "",
      pin: "",
    }))
  }

  const selectCategory = (category) => {
    setForm((current) => ({ ...current, categoryId: category._id, categoryName: "", categoryKind: "generic" }))
  }

  const createCategoryDraft = ({ name, kind }) => {
    setForm((current) => ({ ...current, categoryId: "__new__", categoryName: name, categoryKind: kind }))
  }

  const amountValue = Number(form.amount)
  const customerReady =
    customerMode === "search" ? hasLinkedCustomer : Boolean(form.name && form.phone && form.idNumber && form.pin)
  const unitReady = !unitScopeEnabled || Boolean(form.propertyId && form.unitId)
  const categoryReady = isNewCategory ? Boolean(form.categoryName) : Boolean(form.categoryId)
  const canSubmit = unitReady && customerReady && categoryReady && amountValue > 0

  const submit = async (event) => {
    event.preventDefault()
    if (submitting || !canSubmit) return
    setSubmitting(true)
    setError("")

    const payload = {
      amount: form.amount,
      dueDate: form.dueDate || undefined,
      description: form.description || undefined,
    }
    if (hasLinkedCustomer) {
      payload.customerId = form.customerId
      if (form.idNumber) payload.idNumber = form.idNumber
    } else {
      payload.name = form.name
      payload.phone = form.phone
      payload.idNumber = form.idNumber
      payload.pin = form.pin
    }
    if (isNewCategory) {
      payload.categoryName = form.categoryName
      payload.categoryKind = form.categoryKind
    } else {
      payload.categoryId = form.categoryId
    }
    if (unitScopeEnabled) {
      payload.propertyId = form.propertyId
      payload.unitId = form.unitId
    }

    try {
      await submitBill(providerId, payload)
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to issue bill."))
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Issue a bill"
      subtitle={providerName ? `From ${providerName}` : "Bill a customer"}
      icon="receipt-cutoff"
      onClose={onClose}
      footer={
        <button
          type="submit"
          form="ut-add-bill-form"
          disabled={submitting || !canSubmit}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {submitting ? (
            "Issuing…"
          ) : (
            <>
              <Icon name="send-fill" size="0.8rem" />
              {amountValue > 0 ? `Issue bill · ksh ${formatMoney(amountValue)}` : "Issue bill"}
            </>
          )}
        </button>
      }
    >
      <form id="ut-add-bill-form" onSubmit={submit} className="space-y-5">
        {error && (
          <p className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
            <Icon name="exclamation-circle-fill" />
            {error}
          </p>
        )}

        {unitScopeEnabled && (
          <div className="space-y-2.5">
            <SectionHeader icon="building">Rental unit</SectionHeader>

            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">Property</label>
                <div className="relative">
                  <FieldIcon icon="building" />
                  <select
                    value={form.propertyId}
                    onChange={(event) => {
                      const propertyId = event.target.value
                      setResults([])
                      setCustomerMode("search")
                      setForm((current) => ({
                        ...current,
                        propertyId,
                        unitId: "",
                        customerId: "",
                        customerSearch: "",
                        name: "",
                        phone: "",
                        idNumber: "",
                        pin: "",
                      }))
                    }}
                    className={iconInputClass}
                    required
                  >
                    <option value="">Select property</option>
                    {properties.map((property) => (
                      <option key={property._id} value={property._id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-500">Unit</label>
                <div className="relative">
                  <FieldIcon icon="door-open" />
                  <select
                    value={form.unitId}
                    onChange={(event) => selectUnit(event.target.value)}
                    className={iconInputClass}
                    disabled={!form.propertyId || unitsLoading}
                    required
                  >
                    <option value="">{unitsLoading ? "Loading units..." : "Select occupied unit"}</option>
                    {units.map((unit) => (
                      <option key={unit._id} value={unit._id} disabled={!unit.tenant}>
                        {unit.label || "Unit"}{unit.tenant ? ` - ${unit.tenant.name}` : " - vacant"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {unitsError && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                {unitsError}
              </p>
            )}
            {!unitsLoading && form.propertyId && units.length === 0 && (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                No units found for this property.
              </p>
            )}
          </div>
        )}

        {/* Customer */}
        <div className="space-y-2.5">
          <SectionHeader icon="person-fill">Customer</SectionHeader>

          {hasLinkedCustomer ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
                  {initials(form.name)}
                </span>
                <span className="min-w-0 truncate">
                  <span className="block truncate text-sm font-bold text-emerald-900">{form.name}</span>
                  {/* No "also known as" name is stored for customers yet, so this always reads "---". */}
                  <span className="block truncate text-xs text-emerald-600">AKA: ---</span>
                </span>
              </span>
              <button type="button" onClick={clearLinkedCustomer} className="shrink-0 text-xs font-bold text-emerald-700 underline">
                {unitScopeEnabled ? "Change unit" : "Change"}
              </button>
            </div>
          ) : unitScopeEnabled ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500">
              Select an occupied unit above to choose the customer.
            </p>
          ) : customerMode === "new" ? (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <Icon name="person-plus-fill" size="0.75rem" className="text-teal-600" />
                  New customer
                </p>
                <button
                  type="button"
                  onClick={() => switchMode("search")}
                  className="flex items-center gap-1 text-xs font-bold text-teal-700"
                >
                  <Icon name="arrow-left" size="0.7rem" />
                  Back to search
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="relative">
                  <FieldIcon icon="person" />
                  <input
                    value={form.name}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    className={iconInputClass}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="relative">
                  <FieldIcon icon="telephone" />
                  <input
                    value={form.phone}
                    onChange={(event) => setForm({ ...form, phone: event.target.value })}
                    className={iconInputClass}
                    placeholder="Phone"
                    required
                  />
                </div>
                <div className="relative">
                  <FieldIcon icon="person-vcard" />
                  <input
                    value={form.idNumber}
                    onChange={(event) => setForm({ ...form, idNumber: event.target.value })}
                    className={iconInputClass}
                    placeholder="ID number"
                    required
                  />
                </div>
                <div className="relative">
                  <FieldIcon icon="shield-lock" />
                  <input
                    value={form.pin}
                    onChange={(event) => setForm({ ...form, pin: event.target.value })}
                    className={iconInputClass}
                    placeholder="4-digit PIN"
                    inputMode="numeric"
                    maxLength={4}
                    required
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="relative">
                <FieldIcon icon="search" />
                <input
                  value={form.customerSearch}
                  onChange={(event) => setForm({ ...form, customerSearch: event.target.value })}
                  className={searchInputClass}
                  placeholder="Search by ID number or phone"
                />
                {searching && (
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    <Icon name="arrow-repeat" size="0.85rem" className="animate-spin text-teal-500" />
                  </span>
                )}
              </div>

              {results.length > 0 && (
                <div className="mt-1.5 max-h-44 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                  {results.map((customer) => (
                    <button
                      key={customer._id}
                      type="button"
                      onClick={() => selectCustomer(customer)}
                      className="flex w-full items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-teal-50"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-600">
                        {initials(customer.name)}
                      </span>
                      <span className="min-w-0 truncate">
                        <span className="block truncate text-sm font-semibold text-slate-800">{customer.name}</span>
                        {/* No "also known as" name is stored for customers yet, so this always reads "---". */}
                        <span className="block truncate text-xs text-slate-500">AKA: ---</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {!searching && results.length === 0 && form.customerSearch.trim().length >= 2 && (
                <div className="mt-1.5 flex items-center justify-between gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5">
                  <span className="truncate text-xs text-slate-500">No match for “{form.customerSearch.trim()}”</span>
                  <button type="button" onClick={() => switchMode("new")} className="shrink-0 text-xs font-bold text-teal-700 underline">
                    Add as new
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bill details */}
        <div className="space-y-2.5">
          <SectionHeader icon="cash-coin">Bill details</SectionHeader>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Amount</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                ksh
              </span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(event) => setForm({ ...form, amount: event.target.value })}
                className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-base font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-normal transition-colors focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Category</label>
            <CategoryCombobox
              categories={categories}
              categoryId={form.categoryId}
              categoryName={form.categoryName}
              categoryKind={form.categoryKind}
              onSelectExisting={selectCategory}
              onCreateNew={createCategoryDraft}
            />
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">
                Due date <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <div className="relative">
                <FieldIcon icon="calendar3" />
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(event) => setForm({ ...form, dueDate: event.target.value })}
                  className={iconInputClass}
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">
                Description <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <div className="relative">
                <FieldIcon icon="card-text" />
                <input
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  className={iconInputClass}
                  placeholder="e.g. June water usage"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default AddBillModal
